// Beta promocode context. A code arrives on an inbound URL as ?promocode=X
// (e.g. a beta invite link). We capture it once on entry and hold it in
// in-memory context so it survives navigation between screens (calculator →
// result → auth → back). On web we also stash it in localStorage so a reload
// keeps the code; that persistence is a nice-to-have, not relied on.
//
// This module is intentionally small and isolated: it knows nothing about
// payments. The result screen reads usePromocode() to check the code against
// the API (payments.checkPromocode) and thread it into createPdfCheckout.
import { useGlobalSearchParams } from "expo-router";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY = "aucash.promocode";

const PromocodeContext = createContext<string | null>(null);

function readStored(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage?.getItem(STORAGE_KEY);
    return v !== null && v !== undefined && v !== "" ? v : null;
  } catch {
    // localStorage can throw (private mode, disabled storage) — ignore.
    return null;
  }
}

/** Provides the beta promocode captured from the inbound URL (or persisted on
 * web), seeded once and stable across navigation. */
export function PromocodeProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  // useGlobalSearchParams surfaces query params from anywhere in the route
  // tree, so a ?promocode= on the entry URL is visible here regardless of which
  // screen rendered first.
  const params = useGlobalSearchParams<{ promocode?: string | string[] }>();
  const raw = Array.isArray(params.promocode)
    ? params.promocode[0]
    : params.promocode;
  const fromUrl = raw !== undefined && raw !== "" ? raw : null;

  // Seed from the URL first, falling back to web-persisted storage. Stays put
  // once set — later navigations that drop the query param don't clear it.
  const [code, setCode] = useState<string | null>(() => fromUrl ?? readStored());

  useEffect(() => {
    if (fromUrl === null || fromUrl === code) return;
    setCode(fromUrl);
    if (typeof window !== "undefined") {
      try {
        window.localStorage?.setItem(STORAGE_KEY, fromUrl);
      } catch {
        // Best-effort persistence only.
      }
    }
  }, [fromUrl, code]);

  return (
    <PromocodeContext.Provider value={code}>
      {children}
    </PromocodeContext.Provider>
  );
}

/** The beta promocode in effect for this session, or null if none was supplied. */
export function usePromocode(): string | null {
  return useContext(PromocodeContext);
}
